import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { BarChartCard } from "@/components/charts/BarChartCard";
import { PieChartCard } from "@/components/charts/PieChartCard";
import { PostsTable } from "@/components/tables/PostsTable";
import { TopFBPosts } from "@/components/cards/TopPostCard";
import { EngagementDistribution, WorstPosts, RestaurantLeaderboard, ViralPosts, FollowerTrend } from "@/components/charts/PerformanceAnalytics";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFilteredFB } from "@/hooks/useFilteredData";
import { fillDateRange } from "@/lib/chart-utils";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import { getPreviousPeriod } from "@/lib/period-compare";
import { Eye, MousePointerClick, MessageCircle, Share2, Play, Hash, TrendingUp, BarChart3 } from "lucide-react";
import { LoadingState } from '@/components/ui/spinner';

export default function FacebookPage() {
  const { data, allData, accounts, postTypes, filters, setFilters, loading } = useFilteredFB();
  const posts = data.filter((p) => p.post_id);

  const totalReach = posts.reduce((s, p) => s + p.post_impressions_unique, 0);
  const totalEng = posts.reduce((s, p) => s + p.page_post_engagements, 0);
  const totalClicks = posts.reduce((s, p) => s + p.post_clicks, 0);
  const totalComments = posts.reduce((s, p) => s + p.post_comments_total, 0);
  const totalShares = posts.reduce((s, p) => s + p.post_activity_by_action_type_share, 0);
  const totalVideoViews = posts.reduce((s, p) => s + p.post_video_views, 0);
  const engRate = totalReach > 0 ? totalEng / totalReach : 0;
  const avgReach = posts.length > 0 ? Math.round(totalReach / posts.length) : 0;
  const avgEng = posts.length > 0 ? Math.round(totalEng / posts.length) : 0;

  const prev = useMemo(() => {
    const { dateFrom, dateTo } = getPreviousPeriod(filters.dateFrom, filters.dateTo);
    const pp = allData.filter((p) => p.post_id && p.date >= dateFrom && p.date <= dateTo);
    const reach = pp.reduce((s, p) => s + p.post_impressions_unique, 0);
    const eng = pp.reduce((s, p) => s + p.page_post_engagements, 0);
    return {
      posts: pp.length, reach, eng,
      engRate: reach > 0 ? eng / reach : 0,
      avgReach: pp.length > 0 ? Math.round(reach / pp.length) : 0,
      avgEng: pp.length > 0 ? Math.round(eng / pp.length) : 0,
      clicks: pp.reduce((s, p) => s + p.post_clicks, 0),
      comments: pp.reduce((s, p) => s + p.post_comments_total, 0),
      shares: pp.reduce((s, p) => s + p.post_activity_by_action_type_share, 0),
      video: pp.reduce((s, p) => s + p.post_video_views, 0),
    };
  }, [allData, filters.dateFrom, filters.dateTo]);

  const reachTrend = useMemo(() => {
    const map = new Map<string, number>();
    posts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.post_impressions_unique));
    const raw = Array.from(map.entries()).map(([date, value]) => ({ date, value }));
    return fillDateRange(raw, filters.dateFrom, filters.dateTo);
  }, [posts, filters.dateFrom, filters.dateTo]);

  const postTypePie = useMemo(() => {
    const map = new Map<string, number>();
    posts.forEach((p) => map.set(p.type || "unknown", (map.get(p.type || "unknown") || 0) + 1));
    return Array.from(map.entries()).map(([name, value]) => ({ name: getPostTypeLabel(name), value }));
  }, [posts]);

  const accountData = useMemo(() => {
    const map = new Map<string, { reach: number; engagement: number }>();
    posts.forEach((p) => {
      const curr = map.get(p.account_name) || { reach: 0, engagement: 0 };
      curr.reach += p.post_impressions_unique; curr.engagement += p.page_post_engagements;
      map.set(p.account_name, curr);
    });
    return Array.from(map.entries()).map(([name, { reach, engagement }]) => ({ name, value: reach, value2: engagement }));
  }, [posts]);

  if (loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Facebook" description="Detailní analýza příspěvků" />

      <FilterBar filters={filters} onChange={setFilters} showPostType accounts={accounts} postTypes={postTypes} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Příspěvků" value={posts.length} previousValue={prev.posts} icon={<Hash className="h-4 w-4 text-primary" />} />
        <MetricCard label="Eng. rate" value={engRate} previousValue={prev.engRate} format="percent" icon={<TrendingUp className="h-4 w-4 text-primary" />} />
        <MetricCard label="Ø dosah" value={avgReach} previousValue={prev.avgReach} icon={<Eye className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Ø eng." value={avgEng} previousValue={prev.avgEng} icon={<BarChart3 className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Kliky" value={totalClicks} previousValue={prev.clicks} icon={<MousePointerClick className="h-4 w-4 text-primary" />} />
        <MetricCard label="Komentáře" value={totalComments} previousValue={prev.comments} icon={<MessageCircle className="h-4 w-4 text-primary" />} />
        <MetricCard label="Sdílení" value={totalShares} previousValue={prev.shares} icon={<Share2 className="h-4 w-4 text-primary" />} />
        <MetricCard label="Video views" value={totalVideoViews} previousValue={prev.video} icon={<Play className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Trend dosahu" data={reachTrend} color="#c9a84c" label1="Dosah" fbPosts={posts} />
        <BarChartCard title="Porovnání účtů" data={accountData} color="#c9a84c" color2="#e8c36a" label1="Dosah" label2="Engagement" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PieChartCard title="Typy příspěvků" data={postTypePie} />
        <TopFBPosts posts={posts} limit={3} title="Top engagement" />
        <TopFBPosts posts={posts} limit={3} title="Top dosah" sortBy="reach" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ViralPosts fbPosts={posts} />
        <WorstPosts fbPosts={posts} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementDistribution fbPosts={posts} igPosts={[]} />
        <FollowerTrend fbPosts={data} platform="FB" />
      </div>

      <RestaurantLeaderboard fbPosts={posts} />

      <div>
        <h2 className="text-lg font-semibold mb-4">Všechny příspěvky ({posts.length})</h2>
        <PostsTable posts={posts} platform="facebook" />
      </div>
    </div>
  );
}
